import {
  Search,
  MoreVertical,
  Edit,
  Eye,
  Trash2,
  ChevronDown,
  FileText,
  Calendar,
} from "lucide-react";
import React, { useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { formatPhoneNumber, formatDuration } from "@/app/_module/utils/formatters";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { UnreachableBadge } from "@/app/(authenticated)/leads/_modules/components/UnreachableBadge";
import { ScreeningBadge } from "@/app/(authenticated)/leads/_modules/components/ScreeningBadge";
import { DIAL_IQ_ENABLED } from "@/app/_module/utils/featureFlags";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  ResponsiveAlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/app/_module/components/ui/alert-dialog";
import { Button } from "@/app/_module/components/ui/button";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/_module/components/ui/table";

import getStatusColor from "../constants/get-status-color";
import { useAvailableStatuses, usePipelineStages } from "../hooks/queries";
import { getMostRecentPolicy } from "../utils/get-most-recent-policy";
import { isCfColumn, cfColumnLabel } from "./CustomFieldColumnPicker";

import CreateLeadPolicy from "./CreateLeadPolicy";
import DNCConfirmationModal from "./DNCConfirmationModal";
import NoSaleReasonModal from "./NoSaleReasonModal";
import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import { UpdatePolicyDialog } from "./UpdateLeadPolicy";
import type { Policy as UpdatePolicyType } from "../types/lead-with-policies-type";

interface LeadsTableProps {
  leads: Lead[];
  selectedLeads: string[];
  onSelectAll: (checked: boolean | "indeterminate") => void;
  onSelectLead: (leadId: string, checked: boolean) => void;
  onLeadClick: (lead: Lead) => void;
  onDeleteLead: (leadId: string) => void;
  onStatusUpdate?: (leadId: string, newStatus: string, noSaleReason?: string, blockInbound?: boolean) => void;
  searchTerm: string;
  isLoading: boolean;
  isBulkDeleting?: boolean;
  selectedStatus?: string;
  visibleColumns?: string[];
}

// Helper function to format date in local timezone (avoids UTC shift)
const formatLocalDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to convert Policy from Lead type to UpdatePolicy type
const convertPolicyForEdit = (policy: any): UpdatePolicyType => {
  return {
    id: policy.id,
    policyNumber: policy.policyNumber,
    productType: policy.productType,
    status: policy.status,
    policyStage: policy.policyStage, // Include policyStage for proper form pre-selection
    premium: policy.premium,
    carrierName: policy.carrierName,
    annualPremium: policy.annualPremium,
    issuedAt: policy.issuedAt ? (policy.issuedAt instanceof Date ? formatLocalDateString(policy.issuedAt) : policy.issuedAt) : null,
    expiryDate: policy.expiryDate ? (policy.expiryDate instanceof Date ? formatLocalDateString(policy.expiryDate) : policy.expiryDate) : null,
  } as UpdatePolicyType;
};

// Display name mapping for statuses
const getStatusDisplayName = (status: string): string => {
  const displayNames: Record<string, string> = {
    'New lead': 'Leads',
    'new lead': 'Leads',
  };
  return displayNames[status] || status;
};

// Custom Status Badge Component
const StatusBadge = function ({
  status,
  isDropdown = false,
  isUpdating = false,
  hexColor,
}: {
  status: string;
  isDropdown?: boolean;
  isUpdating?: boolean;
  hexColor?: string;
}) {
  const colorClasses = hexColor ? "" : getStatusColor(status);
  const displayName = getStatusDisplayName(status);
  const inlineStyle = hexColor
    ? { backgroundColor: hexColor, color: '#fff', borderColor: hexColor }
    : undefined;

  return (
    <div
      className={`inline-flex items-center px-3 py-1.5 rounded-lg font-medium text-sm border transition-all duration-200 ${colorClasses} ${
        isDropdown
          ? "cursor-pointer hover:opacity-80 group-hover:shadow-sm"
          : ""
      }`}
      style={inlineStyle}
    >
      <span>{displayName}</span>
      {!!isDropdown && (
        <div className="ml-2 flex items-center">
          {isUpdating ? (
            <div className="h-3 w-3 border border-current border-t-transparent rounded-full animate-spin opacity-70" />
          ) : (
            <ChevronDown className="h-3 w-3 opacity-60 group-hover:opacity-100 transition-opacity" />
          )}
        </div>
      )}
    </div>
  );
};

// Status Dropdown Component
const StatusDropdown = function ({
  currentStatus,
  leadId,
  onStatusChange,
  leads,
  statusesData,
}: {
  currentStatus: string;
  leadId: string;
  onStatusChange: (leadId: string, newStatus: string, noSaleReason?: string, blockInbound?: boolean) => void;
  leads: Lead[];
  statusesData: { statuses: { value: string; label: string; color?: string }[] } | undefined;
}) {
  const [updating, setUpdating] = useState(false);
  const [showDNCModal, setShowDNCModal] = useState(false);
  const [showNoSaleModal, setShowNoSaleModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showEditPolicyModal, setShowEditPolicyModal] = useState(false);
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(
    null
  );
  const [currentLead, setCurrentLead] = useState<Lead | null>(null);
  const [editingPolicy, setEditingPolicy] = useState<UpdatePolicyType | null>(null);

  const statuses = statusesData?.statuses || [
    { value: "New lead", label: "Leads" },
    { value: "Appt", label: "Appointments" },
    { value: "Underwriting", label: "Underwriting" },
    { value: "Sold & Approved", label: "Sold & Approved" },
    { value: "Bad number", label: "Bad number" },
    { value: "No sale", label: "No sale" },
    { value: "Do not call", label: "Do not call" },
    { value: "Declined", label: "Declined" },
    { value: "Lapsed", label: "Lapsed" },
    { value: "Disposed", label: "Disposed" },
  ];
  const loading = !statusesData;

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === currentStatus) return;

    // Check if the new status is "Do not call" or similar DNC statuses
    const dncStatuses = ["Do not call", "DNC", "Do Not Call"];
    const isChangingToDNC = dncStatuses.some((status) =>
      newStatus.toLowerCase().includes(status.toLowerCase())
    );
    const isChangingFromDNC = dncStatuses.some((status) =>
      currentStatus.toLowerCase().includes(status.toLowerCase())
    );

    // Show DNC modal when changing TO DNC OR when changing FROM DNC
    if (isChangingToDNC || isChangingFromDNC) {
      setPendingStatusChange(newStatus);
      setShowDNCModal(true);
      return;
    }

    // Show No Sale reason modal when changing to No sale
    if (newStatus === "No sale") {
      setPendingStatusChange(newStatus);
      setShowNoSaleModal(true);
      return;
    }

    // Show appointment creation dialog when changing to Appt
    if (newStatus === "Appt") {
      const lead = leads.find((l) => l.id === leadId);
      if (lead) {
        setCurrentLead(lead);
        setShowAppointmentDialog(true);
        return;
      }
    }

    // Check if the new status requires policy modal (Sold & Approved, Underwriting, Lapsed, or Declined)
    if (newStatus === "Sold & Approved" || newStatus === "Underwriting" || newStatus === "Lapsed" || newStatus === "Declined") {
      const lead = leads.find((l) => l.id === leadId);
      if (lead) {
        const hasExistingPolicies = lead.policies && lead.policies.length > 0;

        if (hasExistingPolicies) {
          // Lead has policies - convert and open edit modal for the most recent policy
          const mostRecentPolicy = getMostRecentPolicy(lead.policies);

          if (!mostRecentPolicy) {
            return;
          }

          const convertedPolicy = convertPolicyForEdit(mostRecentPolicy);

          setCurrentLead(lead);
          setEditingPolicy(convertedPolicy);
          setPendingStatusChange(newStatus);
          setShowEditPolicyModal(true);
          return;
        }

        // No policies exist, show modal to create one
        setCurrentLead(lead);
        setPendingStatusChange(newStatus);
        setShowPolicyModal(true);
        return;
      }
    }

    // For non-DNC and non-policy statuses, proceed normally
    proceedWithStatusChange(newStatus);
  };

  const proceedWithStatusChange = async (newStatus: string, noSaleReason?: string, blockInbound?: boolean) => {
    setUpdating(true);
    try {
      await onStatusChange(leadId, newStatus, noSaleReason, blockInbound);

      setPendingStatusChange(null);
      setShowDNCModal(false);
      setShowNoSaleModal(false);
      setShowPolicyModal(false);
      setShowEditPolicyModal(false);
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setUpdating(false);
    }
  };

  const handleNoSaleConfirm = async (reason: string) => {
    if (pendingStatusChange) {
      await proceedWithStatusChange(pendingStatusChange, reason);
      setPendingStatusChange(null);
      setShowNoSaleModal(false);
    }
  };

  const handleDNCConfirm = async (blockInbound?: boolean) => {
    if (pendingStatusChange) {
      try {
        await proceedWithStatusChange(pendingStatusChange, undefined, blockInbound);
        setPendingStatusChange(null);
        setShowDNCModal(false);
      } catch (error) {
        throw error;
      }
    }
  };

  const handleDNCRecover = async () => {
    try {
      setPendingStatusChange(null);
      setShowDNCModal(false);
    } catch (error) {
      throw error;
    }
  };

  const handlePolicySave = async (success: boolean) => {
    if (success) {
      if (pendingStatusChange) {
        await proceedWithStatusChange(pendingStatusChange);
        setPendingStatusChange(null);
      }
    } else {
      setPendingStatusChange(null);
    }
  };

  // Handler for editing existing policy
  const handleEditPolicySave = async (success: boolean) => {
    if (success && pendingStatusChange) {
      await proceedWithStatusChange(pendingStatusChange);
    }
    setEditingPolicy(null);
    setPendingStatusChange(null);
    setShowEditPolicyModal(false);
  };

  const asyncOnStatusChange = async (
    leadId: string,
    newStatus: string,
    noSaleReason?: string,
    blockInbound?: boolean,
  ) => {
    onStatusChange(leadId, newStatus, noSaleReason, blockInbound);
  };

  return (
    <React.Fragment>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div className="group cursor-pointer">
            <StatusBadge
              isDropdown
              isUpdating={updating}
              status={currentStatus || "New lead"}
              hexColor={statuses.find(s => s.value === (currentStatus || "New lead"))?.color}
            />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48 p-1 max-h-[320px] overflow-y-auto" sideOffset={4}>
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <div className="h-3 w-3 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
              <span>Loading statuses...</span>
            </div>
          ) : statuses.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No statuses available
            </div>
          ) : (
            <React.Fragment>
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Change Status
              </div>
              {statuses.map((status) => {
                const isSelected = currentStatus === status.value;

                return (
                  <DropdownMenuItem
                    key={status.value}
                    disabled={updating}
                    onClick={() =>
                      !updating && handleStatusChange(status.value)
                    }
                    className={`
                      cursor-pointer rounded-md px-2 py-2 text-sm transition-all duration-150
                      hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground
                      ${isSelected ? "bg-accent/50 font-medium" : ""}
                      ${updating ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <StatusBadge status={status.value} hexColor={status.color} />
                      </div>
                      {!!isSelected && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">
                            Current
                          </span>
                          <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                        </div>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </React.Fragment>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <DNCConfirmationModal
        isOpen={showDNCModal}
        leadName={currentLead?.name}
        onConfirm={handleDNCConfirm}
        onOpenChange={setShowDNCModal}
        onRecover={handleDNCRecover}
        isRecoveringFromDNC={
          currentStatus.toLowerCase().includes("do not call") ||
          currentStatus.toLowerCase().includes("dnc")
        }
        newStatus={pendingStatusChange || undefined}
      />

      <NoSaleReasonModal
        isOpen={showNoSaleModal}
        onOpenChange={(open) => {
          setShowNoSaleModal(open);
          if (!open) setPendingStatusChange(null);
        }}
        onConfirm={handleNoSaleConfirm}
      />

      {/* Appointment Dialog */}
      <NewAppointmentDialog
        open={showAppointmentDialog}
        onOpenChange={(open) => {
          setShowAppointmentDialog(open);
          if (!open) setCurrentLead(null);
        }}
        preselectedLeadId={currentLead?.id?.toString()}
        preselectedLeadName={currentLead?.name}
        preselectedLeadState={currentLead?.state || undefined}
        onStatusChange={asyncOnStatusChange}
      />

      {/* Create Policy Modal */}
      {currentLead && (
        <CreateLeadPolicy
          isOpen={showPolicyModal}
          leadId={currentLead.id}
          leadName={currentLead.name}
          onOpenChange={setShowPolicyModal}
          onSave={handlePolicySave}
          onStatusUpdate={asyncOnStatusChange}
          newStatus={pendingStatusChange || undefined}
        />
      )}

      {/* Edit Policy Modal */}
      {currentLead && (
        <UpdatePolicyDialog
          isOpen={showEditPolicyModal}
          leadId={currentLead.id}
          policy={editingPolicy}
          onOpenChange={(open) => {
            if (!open) {
              setShowEditPolicyModal(false);
              setEditingPolicy(null);
              setPendingStatusChange(null);
            }
          }}
          onSave={handleEditPolicySave}
          onStatusChange={asyncOnStatusChange}
          newStatus={pendingStatusChange || undefined}
        />
      )}
    </React.Fragment>
  );
};
export const LeadsTable = function ({
  leads,
  selectedLeads,
  onSelectAll,
  onSelectLead,
  onLeadClick,
  onDeleteLead,
  onStatusUpdate,
  searchTerm,
  isLoading,
  isBulkDeleting = false,
  selectedStatus,
  visibleColumns,
}: LeadsTableProps) {
  const columns = visibleColumns || ['household', 'phone', 'status', 'policies', 'age', 'lastCall', 'createdAt', 'updatedAt'];
  const cfColumns = columns.filter(isCfColumn);
  const { user } = useUserContext();
  // "Uploaded by" is visible to AGENCY ADMINS ONLY — never owners, never agents.
  // (Owners/assigned agents shouldn't see who uploaded/assigned a lead.)
  const agencyRole = user?.user?.agencyRole || user?.currentAgency?.role;
  const isAgencyAdmin = agencyRole === 'ADMIN' || agencyRole === 'admin';
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedLeadForPolicy, setSelectedLeadForPolicy] =
    useState<Lead | null>(null);

  // Fetch statuses once for all dropdowns (cached for 15 minutes)
  // Don't block the page if this fails - StatusDropdown has fallback statuses
  const { data: statusesData, isError } = useAvailableStatuses();
  const { data: pipelineStages } = usePipelineStages();

  // Merge custom pipeline stages into system statuses
  const mergedStatusesData = React.useMemo(() => {
    const systemStatuses = statusesData?.statuses || [];
    if (!pipelineStages || pipelineStages.length === 0) return statusesData;

    const systemValues = new Set(systemStatuses.map((s: any) => s.value?.toLowerCase()));
    const merged = [...systemStatuses];

    for (const stage of pipelineStages) {
      const stageValue = stage.systemValue || stage.name;
      if (!systemValues.has(stageValue.toLowerCase()) && !systemValues.has(stage.name.toLowerCase())) {
        merged.push({ value: stageValue, label: stage.name, color: stage.color } as any);
      } else {
        const existing = merged.find((s: any) => s.value?.toLowerCase() === stageValue.toLowerCase() || s.value?.toLowerCase() === stage.name.toLowerCase());
        if (existing && stage.color) {
          (existing as any).color = stage.color;
        }
      }
    }

    return { ...statusesData, statuses: merged };
  }, [statusesData, pipelineStages]);

  // Log if status fetch fails (for debugging)
  if (isError) {
  }

  // Batch-fetch appointments for visible leads when viewing Appt status
  const isApptStatus = selectedStatus?.toLowerCase() === 'appt';
  const leadIds = useMemo(() => leads.map(l => l.id), [leads]);

  const { data: appointmentsByLead } = useQuery({
    queryKey: ['lead-appointments-batch', leadIds],
    queryFn: async () => {
      const results: Record<string, { startTime: string; title?: string }> = {};
      // Fetch appointments for each lead in parallel
      const fetches = leadIds.map(async (leadId) => {
        try {
          const baseUrl = getApiUrl(endpoint.appointments.getAppointments);
          const params = new URLSearchParams({
            entityType: 'Lead',
            entityId: leadId,
            page: '1',
            limit: '5',
          });
          const response = await api.get({
            url: `${baseUrl}?${params.toString()}`,
            auth: true,
          });
          // Handle various response nesting: response.items, response.data.items, response.data
          const raw = response as any;
          const items = raw?.items || raw?.data?.items || (Array.isArray(raw?.data) ? raw.data : []);
          // Find the next upcoming appointment (or most recent)
          if (items.length > 0) {
            // Sort by startTime ascending to get the nearest upcoming one
            const sorted = [...items].sort((a: any, b: any) =>
              new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
            );
            // Prefer upcoming appointments, fallback to most recent
            const now = Date.now();
            const upcoming = sorted.find((a: any) => new Date(a.startTime).getTime() >= now);
            const best = upcoming || sorted[sorted.length - 1];
            if (best) {
              results[leadId] = { startTime: best.startTime, title: best.title };
            }
          }
        } catch {
          // Skip failed fetches silently
        }
      });
      await Promise.all(fetches);
      return results;
    },
    enabled: isApptStatus && leadIds.length > 0,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  const handleNameClick = (lead: Lead) => {
    if (!lead) {
      return;
    }

    onLeadClick(lead);
  };

  // Shift-click sets every row between the last clicked box and this one to the clicked box's new state.
  const lastClickedIdRef = useRef<string | null>(null);
  const handleCheckboxClick = (leadId: string, shiftKey: boolean) => {
    const checked = !selectedLeads.includes(leadId);
    const ids = leads.filter((lead) => lead != null).map((lead) => lead.id.toString());
    const from = shiftKey && lastClickedIdRef.current ? ids.indexOf(lastClickedIdRef.current) : -1;
    const to = ids.indexOf(leadId);
    const range = from === -1 || to === -1 ? [leadId] : ids.slice(Math.min(from, to), Math.max(from, to) + 1);
    range.filter((id) => selectedLeads.includes(id) !== checked).forEach((id) => onSelectLead(id, checked));
    lastClickedIdRef.current = leadId;
  };

  const handleDeleteClick = (lead: Lead) => {
    setLeadToDelete(lead);
  };

  const handleConfirmDelete = async () => {
    if (leadToDelete) {
      try {
        await onDeleteLead(leadToDelete.id);
        setLeadToDelete(null);
      } catch (error) {
        // Keep modal open to allow retry
      }
    }
  };

  const handleCancelDelete = () => {
    setLeadToDelete(null);
  };

  // The block-inbound choice is collected by DNCConfirmationModal (which already
  // intercepts every DNC move), so this just forwards whatever it decided.
  const handleStatusChange = (
    leadId: string,
    newStatus: string,
    noSaleReason?: string,
    blockInbound?: boolean,
  ) => {
    if (onStatusUpdate) {
      onStatusUpdate(leadId, newStatus, noSaleReason, blockInbound);
    }
  };


  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="text-muted-foreground">
          <p className="text-lg font-medium">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div data-tour="leads-table" className="bg-card rounded-xl shadow-sm border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead data-tour="leads-table-checkbox" className="w-12 font-semibold">
                </TableHead>
                <TableHead className="font-semibold text-foreground">
                  NAME
                </TableHead>
                {columns.includes('household') && <TableHead className="font-semibold text-foreground text-center">
                  HOUSEHOLD
                </TableHead>}
                {columns.includes('phone') && <TableHead className="font-semibold text-foreground">
                  PHONE
                </TableHead>}
                {columns.includes('status') && <TableHead className="font-semibold text-foreground">
                  STATUS
                </TableHead>}
                {columns.includes('policies') && <TableHead className="font-semibold text-foreground text-center">
                  POLICIES
                </TableHead>}
                {columns.includes('age') && <TableHead className="font-semibold text-foreground text-center">
                  AGE
                </TableHead>}
                {columns.includes('lastCall') && <TableHead className="font-semibold text-foreground whitespace-nowrap">
                  LAST CALL
                </TableHead>}
                {columns.includes('createdAt') && <TableHead className="font-semibold text-foreground whitespace-nowrap">
                  CREATED AT
                </TableHead>}
                {columns.includes('updatedAt') && <TableHead className="font-semibold text-foreground whitespace-nowrap">
                  LAST UPDATED
                </TableHead>}
                {columns.includes('state') && <TableHead className="font-semibold text-foreground">
                  STATE
                </TableHead>}
                {columns.includes('city') && <TableHead className="font-semibold text-foreground">
                  CITY
                </TableHead>}
                {columns.includes('email') && <TableHead className="font-semibold text-foreground">
                  EMAIL
                </TableHead>}
                {columns.includes('dob') && <TableHead className="font-semibold text-foreground">
                  DOB
                </TableHead>}
                {columns.includes('gender') && <TableHead className="font-semibold text-foreground">
                  GENDER
                </TableHead>}
                {columns.includes('listName') && <TableHead className="font-semibold text-foreground whitespace-nowrap">
                  LIST NAME
                </TableHead>}
                {columns.includes('zipCode') && <TableHead className="font-semibold text-foreground whitespace-nowrap">
                  ZIP CODE
                </TableHead>}
                {cfColumns.map((key) => (
                  <TableHead key={key} className="font-semibold text-foreground whitespace-nowrap uppercase">
                    {cfColumnLabel(key)}
                  </TableHead>
                ))}
                <TableHead className="font-semibold text-foreground w-20 text-center">
                  ACTIONS
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads
                .filter((lead) => lead != null)
                .map((lead) => (
                  <TableRow
                    key={lead.id}
                    className="group hover:bg-muted/50 transition-colors duration-200"
                  >
                    <TableCell>
  <Checkbox
    checked={selectedLeads.includes(lead.id.toString())}
    className="border-muted-foreground"
    // Shift + mousedown would also drag a text selection across the rows.
    onMouseDown={(e) => {
      if (e.shiftKey) e.preventDefault();
    }}
    onClick={(e) => {
      e.preventDefault();
      handleCheckboxClick(lead.id.toString(), e.shiftKey);
    }}
  />
</TableCell>
<TableCell>
  <div className="flex flex-col">
    <div className="flex items-center gap-1.5">
      <button
        className="text-gray-900 font-semibold cursor-pointer hover:underline transition-all duration-200 hover:text-gray-700 text-left truncate max-w-[180px] block"
        onClick={() => handleNameClick(lead)}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleNameClick(lead);
          }
        }}
      >
        {lead.name || "N/A"}
      </button>
      {DIAL_IQ_ENABLED && (
        <UnreachableBadge
          leadId={lead.id.toString()}
          tags={lead.tags}
          leadName={lead.name}
          compact
        />
      )}
      <ScreeningBadge
        leadId={lead.id.toString()}
        tags={lead.tags}
        leadName={lead.name}
        compact
      />
    </div>
    {isAgencyAdmin && lead.uploadedByName && lead.uploadedByName !== user?.user?.name && (
      <span className="text-xs text-gray-400">Uploaded by {lead.uploadedByName}</span>
    )}
    {isApptStatus && appointmentsByLead?.[lead.id] && (() => {
      const appt = appointmentsByLead[lead.id];
      const apptDate = new Date(appt.startTime);
      const now = new Date();
      const isUpcoming = apptDate.getTime() >= now.getTime();
      const isPast = !isUpcoming;
      const isToday = apptDate.toDateString() === now.toDateString();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const isTomorrow = apptDate.toDateString() === tomorrow.toDateString();

      // Badge shows YOUR (agent) local time
      let dateLabel: string;
      if (isToday) {
        dateLabel = `Today ${apptDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
      } else if (isTomorrow) {
        dateLabel = `Tomorrow ${apptDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
      } else {
        dateLabel = apptDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      }

      // Tooltip shows CLIENT's local time based on their state
      const stateToTimezone: Record<string, string> = {
        CT: 'America/New_York', DE: 'America/New_York', DC: 'America/New_York',
        FL: 'America/New_York', GA: 'America/New_York', IN: 'America/Indiana/Indianapolis',
        KY: 'America/New_York', ME: 'America/New_York', MD: 'America/New_York',
        MA: 'America/New_York', MI: 'America/New_York', NH: 'America/New_York',
        NJ: 'America/New_York', NY: 'America/New_York', NC: 'America/New_York',
        OH: 'America/New_York', PA: 'America/New_York', RI: 'America/New_York',
        SC: 'America/New_York', VT: 'America/New_York', VA: 'America/New_York',
        WV: 'America/New_York',
        AL: 'America/Chicago', AR: 'America/Chicago', IL: 'America/Chicago',
        IA: 'America/Chicago', KS: 'America/Chicago', LA: 'America/Chicago',
        MN: 'America/Chicago', MS: 'America/Chicago', MO: 'America/Chicago',
        NE: 'America/Chicago', ND: 'America/Chicago', OK: 'America/Chicago',
        SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago',
        WI: 'America/Chicago',
        AZ: 'America/Phoenix', CO: 'America/Denver', ID: 'America/Boise',
        MT: 'America/Denver', NM: 'America/Denver', UT: 'America/Denver',
        WY: 'America/Denver',
        CA: 'America/Los_Angeles', NV: 'America/Los_Angeles',
        OR: 'America/Los_Angeles', WA: 'America/Los_Angeles',
        AK: 'America/Anchorage', HI: 'Pacific/Honolulu',
      };
      const leadState = lead.state?.toUpperCase()?.trim();
      const leadTz = leadState ? stateToTimezone[leadState] : undefined;

      let tooltipText = '';
      if (leadTz) {
        const clientTime = apptDate.toLocaleString('en-US', {
          timeZone: leadTz,
          month: 'short', day: 'numeric',
          hour: 'numeric', minute: '2-digit',
          timeZoneName: 'short',
        });
        tooltipText = `Client's time: ${clientTime}`;
      }

      return (
        <span
          className={`inline-flex items-center gap-1 text-[11px] mt-0.5 ${isPast ? 'text-red-500' : isToday ? 'text-green-600 font-medium' : 'text-blue-600'}`}
          title={tooltipText || undefined}
        >
          <Calendar className="h-3 w-3" />
          {dateLabel}
        </span>
      );
    })()}
  </div>
</TableCell>
{columns.includes('household') && <TableCell className="text-center">
  {lead.hasHousehold ? (
    <div className="inline-flex flex-col items-center">
      <span className="px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
        Yes
      </span>
      {lead.isPrimaryHouseholdMember && (
        <span className="text-[10px] text-blue-600 mt-0.5">Primary</span>
      )}
      {lead.isHouseholdMember && !lead.isPrimaryHouseholdMember && (
        <span className="text-[10px] text-blue-600 mt-0.5">Member</span>
      )}
    </div>
  ) : (
    <span className="px-2 py-1 rounded-md bg-gray-50 text-gray-500 text-xs">
      No
    </span>
  )}
</TableCell>}
{columns.includes('phone') && <TableCell>
  <span className="text-sm text-foreground whitespace-nowrap inline-flex items-center gap-1.5">
    {lead.phone ? formatPhoneNumber(lead.phone) : "N/A"}
    {(lead as any).dncFlagged && (
      <span title="On the Do-Not-Call list (DNC scrub)" className="rounded px-1 py-0.5 text-[9px] font-bold bg-red-100 text-red-700 border border-red-200 leading-none">DNC</span>
    )}
    {!(lead as any).dncFlagged && (lead as any).dncRisky && (
      <span title="Risky number — scrub recommends not dialing" className="rounded px-1 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-200 leading-none">RISK</span>
    )}
  </span>
</TableCell>}
{columns.includes('status') && <TableCell>
  <div className="flex flex-col gap-1">
    <StatusDropdown
      currentStatus={lead.leadStat || "New lead"}
      leadId={lead.id.toString()}
      leads={leads}
      onStatusChange={handleStatusChange}
      statusesData={mergedStatusesData}
    />
    {lead.noSaleReason && lead.leadStat === "No sale" && (
      <span className="text-[10px] text-gray-500 truncate max-w-[140px]" title={lead.noSaleReason}>
        {lead.noSaleReason}
      </span>
    )}
  </div>
</TableCell>}
{columns.includes('policies') && <TableCell className="text-center">
  <span className="text-sm font-medium">
    {lead.policies?.length || 0}
  </span>
</TableCell>}
{columns.includes('age') && <TableCell className="text-center text-muted-foreground">
  <span className="text-sm">{lead.age || "-"}</span>
</TableCell>}
{columns.includes('lastCall') && <TableCell className="text-muted-foreground">
  <span className="text-sm whitespace-nowrap">
    {lead.lastCall
      ? new Date(lead.lastCall).toLocaleDateString(
          "en-US",
          {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }
        )
      : "-"}
  </span>
</TableCell>}
{columns.includes('createdAt') && <TableCell>
  <span className="text-sm text-muted-foreground whitespace-nowrap">
    {lead.createdAt
      ? new Date(lead.createdAt).toLocaleDateString()
      : "N/A"}
  </span>
</TableCell>}
{columns.includes('updatedAt') && <TableCell>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {lead.updatedAt
                          ? new Date(lead.updatedAt).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </TableCell>}
{columns.includes('state') && <TableCell>
  <span className="text-sm text-muted-foreground">{lead.state || "-"}</span>
</TableCell>}
{columns.includes('city') && <TableCell>
  <span className="text-sm text-muted-foreground">{lead.city || "-"}</span>
</TableCell>}
{columns.includes('email') && <TableCell>
  <span className="text-sm text-muted-foreground">{lead.email || "-"}</span>
</TableCell>}
{columns.includes('dob') && <TableCell>
  <span className="text-sm text-muted-foreground">{lead.dob ? new Date(lead.dob).toLocaleDateString() : "-"}</span>
</TableCell>}
{columns.includes('gender') && <TableCell>
  <span className="text-sm text-muted-foreground">{lead.gender || "-"}</span>
</TableCell>}
{columns.includes('listName') && <TableCell>
  <span className="text-sm text-muted-foreground">{lead.listName || "-"}</span>
</TableCell>}
{columns.includes('zipCode') && <TableCell>
  <span className="text-sm text-muted-foreground">{lead.zipCode || "-"}</span>
</TableCell>}
{cfColumns.map((key) => {
  const v = (lead as any).customFields?.[cfColumnLabel(key)];
  return (
    <TableCell key={key}>
      <span className="text-sm text-muted-foreground whitespace-nowrap">{v == null || v === "" ? "-" : String(v)}</span>
    </TableCell>
  );
})}
                    <TableCell className="text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            className="h-8 w-8 p-0"
                            size="sm"
                            variant="ghost"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => handleNameClick(lead)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => handleNameClick(lead)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Lead
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={() => {
                              setSelectedLeadForPolicy(lead);
                              setShowPolicyModal(true);
                            }}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            New Policy
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="cursor-pointer text-destructive"
                            onClick={() => handleDeleteClick(lead)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

        {/* Enhanced Footer */}
        {leads.length === 0 && !!searchTerm && (
          <div className="p-8 text-center">
            <div className="text-muted-foreground">
              <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No leads found</p>
              <p className="text-sm">Try adjusting your search terms</p>
            </div>
          </div>
        )}

        {/* No leads message - show loading during bulk delete, otherwise show empty state */}
        {leads.length === 0 && !searchTerm && (
          <div className="p-8 text-center">
            {isBulkDeleting ? (
              <div className="text-muted-foreground">
                <div className="h-8 w-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-lg font-medium">Deleting leads...</p>
                <p className="text-sm">Please wait while we process your request</p>
              </div>
            ) : (
              <div className="text-muted-foreground">
                <p className="text-lg font-medium">No leads available</p>
                <p className="text-sm">Create your first lead to get started</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        onOpenChange={() => setLeadToDelete(null)}
        open={!!leadToDelete}
      >
        <ResponsiveAlertDialogContent size="md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete `{leadToDelete?.name}`? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelDelete}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </ResponsiveAlertDialogContent>
      </AlertDialog>

      {/* Policy Modal */}
      {selectedLeadForPolicy && (
        <CreateLeadPolicy
          isOpen={showPolicyModal}
          leadId={selectedLeadForPolicy.id}
          leadName={selectedLeadForPolicy.name}
          onOpenChange={setShowPolicyModal}
          onSave={() => {
            setShowPolicyModal(false);
            setSelectedLeadForPolicy(null);
          }}
        />
      )}
    </React.Fragment>
  );
};
